import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { GoogleMap, DirectionsRenderer } from '@react-google-maps/api';
import { useMaps } from '../MapsProvider.jsx';

const DEFAULT_CENTER = { lat: 53.7267, lng: -127.6476 };

export default function MovdRouteMap({ origin, destination, onRouteChange }) {
  const { isLoaded, loadError, authError } = useMaps();
  const [status, setStatus] = useState('idle');
  const [routeDistance, setRouteDistance] = useState(null);
  const [routeDuration, setRouteDuration] = useState(null);
  const [directions, setDirections] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const mapId = useMemo(() => `movd-route-${Math.random().toString(36).slice(2)}`, []);

  useEffect(() => {
    if (!isLoaded || loadError || authError || !window.google?.maps) {
      setDirections(null);
      setRouteDistance(null);
      setRouteDuration(null);
      return undefined;
    }

    const originText = typeof origin === 'string' ? origin : origin?.formatted_address || origin?.address || '';
    const destinationText = typeof destination === 'string' ? destination : destination?.formatted_address || destination?.address || '';

    if (!originText || !destinationText) {
      setStatus('idle');
      setDirections(null);
      setRouteDistance(null);
      setRouteDuration(null);
      onRouteChange?.({ distanceKm: null, durationMinutes: null });
      return undefined;
    }

    const directionsService = new window.google.maps.DirectionsService();
    setStatus('routing');

    directionsService.route(
      {
        origin: originText,
        destination: destinationText,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, routeStatus) => {
        if (routeStatus === 'OK' && result?.routes?.[0]) {
          const leg = result.routes[0].legs[0];
          const distanceKm = leg.distance?.value ? leg.distance.value / 1000 : null;
          const durationMinutes = leg.duration?.value ? leg.duration.value / 60 : null;

          setDirections(result);
          setRouteDistance(distanceKm !== null ? `${Math.round(distanceKm).toLocaleString()} km` : null);
          setRouteDuration(durationMinutes !== null ? `${Math.round(durationMinutes / 60)} hr ${Math.round(durationMinutes % 60)} min` : null);
          onRouteChange?.({ distanceKm, durationMinutes });
          setStatus('ready');
          return;
        }

        setDirections(null);
        setRouteDistance(null);
        setRouteDuration(null);
        onRouteChange?.({ distanceKm: null, durationMinutes: null });
        setStatus(routeStatus === 'ZERO_RESULTS' ? 'invalid' : 'unavailable');
      }
    );
  }, [authError, destination, isLoaded, loadError, onRouteChange, origin]);

  useEffect(() => {
    if (mapInstance && window.google?.maps && directions) {
      const bounds = new window.google.maps.LatLngBounds();
      directions.routes[0].legs.forEach((leg) => {
        bounds.extend(leg.start_location);
        bounds.extend(leg.end_location);
      });
      mapInstance.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
    }
  }, [directions, mapInstance]);

  if (loadError || authError || !isLoaded) {
    return (
      <Box sx={{ position: 'relative', height: '100%', minHeight: 200, bgcolor: '#102A3A', overflow: 'hidden', '& .leaflet-container': { width: '100%', height: '100%', background: '#102A3A', fontFamily: 'inherit' }, '& .leaflet-control-zoom': { border: '1px solid rgba(255,255,255,.18) !important', margin: '0 18px 18px 0 !important' }, '& .leaflet-control-zoom a': { background: '#102A3A', color: '#F8FAFC', borderColor: 'rgba(255,255,255,.12)' } }}>
        <div id={mapId} />
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(135deg, rgba(7,17,31,.42), transparent 48%, rgba(85,214,190,.08))' }} />
        <Box sx={{ position: 'absolute', top: 18, left: 18, right: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pointerEvents: 'none' }}>
          <Box sx={{ bgcolor: 'rgba(7,17,31,.9)', borderLeft: '3px solid #55D6BE', px: 1.5, py: 1.25 }}>
            <Typography sx={{ color: '#55D6BE', fontSize: '.64rem', fontWeight: 900, letterSpacing: '.16em' }}>MOVD ROUTE SYSTEM</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,.74)', fontSize: '.76rem', mt: .35 }}>Waiting for map provider</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', minHeight: 200, bgcolor: '#102A3A', overflow: 'hidden' }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%', minHeight: 200, borderRadius: 0 }}
        center={DEFAULT_CENTER}
        zoom={4}
        options={{
          disableDefaultUI: true,
          gestureHandling: 'cooperative',
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        }}
        onLoad={setMapInstance}
      >
        {directions && <DirectionsRenderer directions={directions} options={{ suppressMarkers: false }} />}
      </GoogleMap>

      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(135deg, rgba(7,17,31,.42), transparent 48%, rgba(85,214,190,.08))' }} />
      <Box sx={{ position: 'absolute', top: 18, left: 18, right: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pointerEvents: 'none' }}>
        <Box sx={{ bgcolor: 'rgba(7,17,31,.9)', borderLeft: '3px solid #55D6BE', px: 1.5, py: 1.25 }}>
          <Typography sx={{ color: '#55D6BE', fontSize: '.64rem', fontWeight: 900, letterSpacing: '.16em' }}>MOVD ROUTE SYSTEM</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,.74)', fontSize: '.76rem', mt: .35 }}>{status === 'routing' ? 'Plotting your route' : status === 'ready' ? 'Route ready' : status === 'invalid' ? 'Check the locations' : status === 'unavailable' ? 'Map route unavailable' : 'Waiting for your route'}</Typography>
        </Box>
        {routeDistance && (
          <Box sx={{ display: 'flex', gap: .75, alignItems: 'center' }}>
            <Typography sx={{ bgcolor: '#55D6BE', color: '#07111F', px: 1.25, py: .8, fontSize: '.72rem', fontWeight: 900 }}>{routeDistance}</Typography>
            <Typography sx={{ bgcolor: 'rgba(7,17,31,.9)', color: '#F8FAFC', px: 1.25, py: .8, fontSize: '.72rem', fontWeight: 800 }}>{routeDuration}</Typography>
          </Box>
        )}
      </Box>
      {(!origin || !destination) && <Box sx={{ position: 'absolute', left: 18, bottom: 18, bgcolor: 'rgba(7,17,31,.88)', px: 1.5, py: 1, color: 'rgba(255,255,255,.72)', fontSize: '.76rem' }}>Add both ends of the move to draw the route.</Box>}
    </Box>
  );
}
